/* oxlint-disable react/jsx-pascal-case */
import React, { useCallback, useMemo } from 'react';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { CopyToClipboardButton } from 'ui-components/components/copy-to-clipboard-button/copy-to-clipboard-button';
import { FlexRow } from 'ui-components/components/flex';
import { InteractablePopover } from 'ui-components/components/interactable-popover';
import { Pill } from 'ui-components/components/pill';

import CarrierActions from './carrier-actions';
import style from './carriers-wallet.module.less';
import DataDrivenLogo from '../../../../components/data-driven-logo';
import getCarrierDisplayName from '../../../../components/data-driven-logo/get-carrier-display-name';
import getDisplayInformation from '../../../../components/data-driven-logo/get-display-information';
import { getSanitizedName } from '../../../../components/data-driven-logo/name-utils';
import { US } from '../../../../constants/country-codes';
import { useGlobalModalLaunchContext } from '../../../../contexts/modal-context/global-modal-context';
import { nonDefaultWalletSellerLabelProviderIdsSet } from '../../../../enums/providers';
import { useModal } from '../../../../hooks/use-modal';
import { useSellerLabelProvider } from '../../../../hooks/use-seller-label-provider';
import { useSellerLabelProviders } from '../../../../hooks/use-seller-label-providers';
import { HelpCenterService } from '../../../../services/helpcenter-service';
import Global from '../../../../store/global-selectors';
import { refreshLabelProviders } from '../../../../store/seller/seller.actions';
import { useWallet } from '../../../../wallet/hooks/use-wallet';
import { NewFeatureBadge } from '../../components/new-feature-badge';
import { TD } from '../../providers/providers-list/reuseable-table/components';
import ServiceText from '../../providers/providers-list/service-text';
import { useServiceVisibilityContext } from '../../providers/service-visibility-modal/context-provider';
import FeaturesModal from '../features-modal';
import { useTrackEvent, PageEvents, PageEventSourceStrings } from '../hooks/use-track-event';
import { CarrierRegistrationError } from './carrier-registration-error';
import messages from '../messages';

export const ActiveStatus = 'Active';
export const HiddenStatus = 'Hidden';

/**
 * This registration error contains content intended for an internal audience and an external audience. This function
 * strips out the internal content and returns the external content.
 *
 * Truncates the carrier registration error message to remove the redundant "Failed to register a new UPS..." prefix and the "Failed at..." suffix.
 * @param {string} registrationError - the registration error message
 * @returns {string}
 */
export const truncateCarrierRegistrationErrorMessage = (registrationError) => {
  if (!registrationError) {
    return '';
  }
  if (registrationError.startsWith('Failed')) {
    return registrationError.substring(registrationError.indexOf('.') + 2, registrationError.indexOf('. Failed')) ?? registrationError;
  }
  const errorSansTags = registrationError.replace(/<br\/>/g, ' ');
  return errorSansTags.substring(0, errorSansTags.indexOf('. Failed')) ?? errorSansTags;
};

const getDisabledTooltipMessage = (
  registrationError,
  isPendingApprovalToCreateLabel,
  showNewIcon,
  isAutoProvisionedProvider,
  providerName,
) => {
  if (isPendingApprovalToCreateLabel) {
    return <FormattedMessage {...messages.activateCarrierBlocked} values={{ providerName }} />;
  }
  if (registrationError) {
    return <FormattedMessage {...messages.activateCarrierBlockedUnknown} />;
  }
  if (showNewIcon || isAutoProvisionedProvider) {
    return <FormattedMessage {...messages.activateCarrierNoRate} />;
  }
  return <FormattedMessage {...messages.activateCarrierLong} />;
};

/**
 * @param {object} props
 * @param {string} [props.walletId] - The wallet ID
 * @param {string | number} props.providerId - The provider ID (required)
 * @param {string | number} [props.sellerLabelProviderId] - The seller label provider ID
 * @param {(sellerLabelProviderId: string | number, isActiveCarrier: bool) => void} props.updateProviderStatus - Function to update provider status (required)
 * @param {boolean} [props.showCarrierId] - Whether to show the carrier ID column
 * @returns {React.JSX.Element}
 */
export const CarrierRow = ({ walletId = '', providerId, sellerLabelProviderId = '', updateProviderStatus, showCarrierId = false }) => {
  const dispatch = useDispatch();
  const carrierDisplayInfo = getDisplayInformation({ providerId });
  const countryCode = useSelector(Global.selectSellerHomeCountry);
  const featuresForCountry = carrierDisplayInfo?.carrierFeatures?.find((code) => code.country === countryCode) || {};
  const { featureList = [] } = featuresForCountry;
  const { isOpen: isFeaturesModalOpen, open: openFeaturesModal, close: closeFeaturesModal } = useModal();
  const { openBillingInfoModal } = useGlobalModalLaunchContext();

  const sanitizeBranding = true;
  const carrierNameMessageDescriptor = getCarrierDisplayName(carrierDisplayInfo?.carrierId, sanitizeBranding);
  const sellerLabelProvidersForProvider = useSellerLabelProviders(providerId);
  const hasMultipleAccountsForProvider = sellerLabelProvidersForProvider.length > 1;
  const sellerLabelProvider = useSellerLabelProvider(sellerLabelProviderId);
  const { isPrimaryAccount, status, registrationError, isPendingApprovalToCreateLabel, requiresSetup } = sellerLabelProvider || {};
  const showDefaultIndicator = isPrimaryAccount && hasMultipleAccountsForProvider;
  const requireExactWallet = true;
  const { isWalletRegistered } = useWallet(walletId, requireExactWallet);
  const showNewIcon = isWalletRegistered && requiresSetup;
  const cleanName = getSanitizedName(carrierDisplayInfo?.name || '');
  const isAutoProvisionedProvider =
    nonDefaultWalletSellerLabelProviderIdsSet.has(providerId.toString()) && requiresSetup && !isWalletRegistered;

  const isActiveCarrier =
    status === ActiveStatus && !registrationError && !isPendingApprovalToCreateLabel && !showNewIcon && !isAutoProvisionedProvider;
  const isActivateDisabled =
    !sellerLabelProvider || registrationError || isPendingApprovalToCreateLabel || showNewIcon || isAutoProvisionedProvider;

  const { onManageServices } = useServiceVisibilityContext();
  const openServiceModal = useCallback(
    () => onManageServices(providerId, sellerLabelProviderId),
    [providerId, onManageServices, sellerLabelProviderId],
  );
  const refreshSellerProviders = useCallback(() => dispatch(refreshLabelProviders({ shouldRefreshServices: true })), [dispatch]);

  const trackToggleCarrier = useTrackEvent(PageEvents.TOGGLE_WALLET_LABEL_PROVIDER, PageEventSourceStrings.SETTINGS_CARRIERS_PAGE);
  const toggleCarrier = useCallback(async () => {
    const result = await updateProviderStatus(sellerLabelProviderId, isActiveCarrier);
    if (result !== null) {
      refreshSellerProviders();
    }

    const nextStatus = isActiveCarrier ? HiddenStatus : ActiveStatus;

    trackToggleCarrier({ providerId, status: nextStatus });
  }, [updateProviderStatus, sellerLabelProviderId, isActiveCarrier, trackToggleCarrier, providerId, refreshSellerProviders]);

  const truncatedRegistrationErrorMessage = useMemo(() => {
    return truncateCarrierRegistrationErrorMessage(registrationError);
  }, [registrationError]);
  const isFraudError = truncatedRegistrationErrorMessage?.includes('Please contact support');
  const supportUrl = HelpCenterService.getMainSupportUrl(US);
  const _openWalletPaymentUpdate = useCallback(() => {
    openBillingInfoModal({
      trackingSourceString: PageEventSourceStrings.SETTINGS_CARRIERS_PAGE_BANNER,
      transactionCategory: TransactionCategory.AddFunds,
    });
  }, [openBillingInfoModal]);

  return (
    <tr>
      <TD className={style['provider-name-cell']}>
        <FlexRow className={style['first-col']}>
          <CheckboxSlider
            checkboxId={providerId}
            checked={isActiveCarrier}
            disabled={isActivateDisabled}
            disabledTooltipMessage={getDisabledTooltipMessage(
              registrationError,
              isPendingApprovalToCreateLabel,
              showNewIcon,
              isAutoProvisionedProvider,
              cleanName,
            )}
            onChange={toggleCarrier}
          />
          <div className={classnames(style['provider-row-logo'], { [style.disabled]: !isActiveCarrier })} data-testid='logo-container'>
            <DataDrivenLogo logoType='icon' providerId={Number.parseInt(providerId, 10)} />
          </div>
          <div className={classnames(style['name-column'], { [style.disabled]: !isActiveCarrier })}>
            <div>
              <FormattedMessage {...carrierNameMessageDescriptor} />
            </div>
            {showDefaultIndicator ? (
              <div className={style['balance-label']}>
                <FormattedMessage {...messages.default} />
              </div>
            ) : null}
          </div>
          {showNewIcon ? <NewFeatureBadge /> : null}
          {isPendingApprovalToCreateLabel ? (
            <InteractablePopover
              hideTooltipTitle
              ignoreFlyout
              popoverBody={<FormattedMessage {...messages.activateCarrierBlocked} values={{ providerName: cleanName }} />}
              position='top'
              shouldBeDark
              trigger='hover'
            >
              <Pill className={style.pending} label={<FormattedMessage {...messages.pending} />} />
            </InteractablePopover>
          ) : null}
        </FlexRow>
        {registrationError ? (
          <CarrierRegistrationError
            isFraudError={isFraudError}
            openWalletPaymentUpdate={_openWalletPaymentUpdate}
            supportUrl={supportUrl}
            truncatedRegistrationErrorMessage={truncatedRegistrationErrorMessage}
            walletId={walletId}
          />
        ) : null}
      </TD>
      <TD>
        <div>
          <Button className={style['link-size']} disabled={!isActiveCarrier} onClick={openServiceModal} theme='link'>
            <ServiceText sellerLabelProviderId={sellerLabelProviderId} />
          </Button>
        </div>
      </TD>
      <TD className={style['feature-cell']}>
        <>
          {!!featureList.length && (
            <ul>
              <li>{featureList[0]}</li>
              <li>
                {featureList[1]}
                {featureList.length > 2 && (
                  <FormattedMessage
                    {...messages.andMore}
                    values={{
                      link: (chunks) => (
                        <Button className={style['link-size']} onClick={openFeaturesModal} theme='link'>
                          {chunks}
                        </Button>
                      ),
                    }}
                  />
                )}
              </li>
            </ul>
          )}
          {isFeaturesModalOpen ? (
            <FeaturesModal
              carrierName={<FormattedMessage {...carrierNameMessageDescriptor} />}
              close={closeFeaturesModal}
              features={featureList}
            />
          ) : null}
        </>
      </TD>
      {showCarrierId ? (
        <TD className={style['id-cell']}>
          {sellerLabelProviderId ? (
            <>
              <span>{`se-${sellerLabelProviderId}`}</span>
              <CopyToClipboardButton
                textToCopy={`se-${sellerLabelProviderId}`}
                tooltipMessageDescriptor={messages.copyCarrierIdButtonLabel}
                tooltipPosition='top-right'
              />
            </>
          ) : null}
        </TD>
      ) : null}
      <TD className={style['actions-cell']}>
        <CarrierActions isActiveCarrier={isActiveCarrier} sellerLabelProvider={sellerLabelProvider} walletId={walletId} />
      </TD>
    </tr>
  );
};
